/**
 * Gemeinsame Berechnung der Gesamtbewertung ("Gesamt-Günnies").
 *
 * WICHTIG: Diese Datei wird sowohl vom Backend (Lambda-Handler) als auch vom
 * Frontend (Live-Vorschau im Formular) importiert. Änderungen an der Formel
 * hier wirken automatisch an beiden Stellen — genau das ist so gewollt
 * (siehe technical-design.md, Abschnitt 4.4).
 *
 * Regel: gleichgewichteter Mittelwert ALLER abgegebenen Einzelnoten
 * (4 Pflicht + jede ausgefüllte optionale), gerundet auf 1 Nachkommastelle.
 */

export const PFLICHT_KRITERIEN = [
  'noteSpaghettiOptik',
  'noteKonsistenz',
  'noteGeschmack',
  'noteFruchtsauce',
] as const;

export const OPTIONALE_KRITERIEN = [
  'noteWeisseRaspel',
  'noteSahne',
  'notePortionsgroesse',
  'notePraesentation',
  'notePreisLeistung',
] as const;

export const ALLE_KRITERIEN = [...PFLICHT_KRITERIEN, ...OPTIONALE_KRITERIEN] as const;

export type Kriterium = (typeof ALLE_KRITERIEN)[number];

export type Einzelnoten = Partial<Record<Kriterium, number | null | undefined>>;

/** Eine gültige Einzelnote ist eine ganze Zahl 1..5. */
export function istGueltigeNote(wert: unknown): wert is number {
  return typeof wert === 'number' && Number.isInteger(wert) && wert >= 1 && wert <= 5;
}

/**
 * Berechnet die Gesamt-Günnie-Zahl aus den abgegebenen Einzelnoten.
 * Gibt `null` zurück, solange noch keine gültige Note vorliegt.
 */
export function berechneGesamtGuennies(noten: Einzelnoten): number | null {
  const werte = ALLE_KRITERIEN.map((k) => noten[k]).filter(istGueltigeNote);
  if (werte.length === 0) return null;
  const summe = werte.reduce((a, b) => a + b, 0);
  return Math.round((summe / werte.length) * 10) / 10;
}

/** Prüft, ob alle Pflichtkriterien gültig gesetzt sind. */
export function pflichtnotenVollstaendig(noten: Einzelnoten): boolean {
  return PFLICHT_KRITERIEN.every((k) => istGueltigeNote(noten[k]));
}
